// The drawing half of routeLineStyle.ts's config: every consumer (the public
// MapView.vue, the admin "Стиль маршрутов" preview in RouteStyleSection.vue,
// RouteMiniMap.vue, ImportPointsTool.vue) renders through this one module,
// so there is exactly one definition of what a route is supposed to look
// like and none of them can drift from another.
//
// MapLibre wants GeoJSON in named sources plus paint properties on stacked
// layers, rather than drawing directly into a layer group - so this module
// is split in two: buildRouteLineFeatures() produces the geometry,
// buildRouteLinePaint() produces the per-layer paint. Each consumer owns its
// own sources/layers (see MapView.vue's ROUTE_SOURCES for the fullest
// example).
import {
  buildGradientSegments,
  colorForDistance,
  computeArrowPlacements,
  destinationPoint,
} from "./routeLineStyle";
import type { PointLLE, RouteStyleArrows, RouteStyleConfig } from "./routeLineStyle";
// Imported rather than used as the ambient GeoJSON namespace: tsconfig pins
// `types` to vite/client, so @types/geojson's globals aren't auto-included.
import type { Feature, LineString, Point } from "geojson";

export type LineFeature = Feature<LineString, { color: string }>;
export type ArrowFeature = Feature<Point, { bearing: number }>;

export interface RouteLineFeatures {
  glow: LineFeature[];
  casing: LineFeature[];
  main: LineFeature[];
  arrows: ArrowFeature[];
}

export const ROUTE_ARROW_IMAGE_ID = "route-arrow";

const lineFeature = (coordinates: [number, number][], color: string): LineFeature => ({
  type: "Feature",
  properties: { color },
  geometry: { type: "LineString", coordinates },
});

// GeoJSON is [lng, lat]; PointLLE (and every other bit of this app's route
// math) is [lat, lng]. This is the single conversion point - everything
// downstream of here is GeoJSON order.
const toLngLat = (points: PointLLE[]): [number, number][] =>
  points.map(([lat, lng]) => [lng, lat]);

export function buildRouteLineFeatures(
  points: PointLLE[],
  cfg: RouteStyleConfig
): RouteLineFeatures {
  const empty: RouteLineFeatures = { glow: [], casing: [], main: [], arrows: [] };
  if (points.length < 2) return empty;

  const full = toLngLat(points);
  const features: RouteLineFeatures = {
    glow: cfg.glow.enabled ? [lineFeature(full, cfg.glow.color)] : [],
    casing: cfg.casing.enabled ? [lineFeature(full, cfg.casing.color)] : [],
    main: [],
    arrows: [],
  };

  const gradientSegments =
    cfg.mode === "gradient" ? buildGradientSegments(points, cfg.gradient) : null;

  if (cfg.main.style === "arrows") {
    // Each chevron is its own two-segment LineString, exactly as the Leaflet
    // renderArrowLine draws it - the wings are placed in real ground metres
    // so they keep their proportions at every zoom.
    for (const placement of computeArrowPlacements(points, cfg.main.arrowSpacingM)) {
      const color = gradientSegments
        ? colorForDistance(gradientSegments, placement.distanceM, cfg.main.color)
        : cfg.main.color;
      const back = (placement.bearing + 180) % 360;
      const left = destinationPoint(
        placement.lat,
        placement.lng,
        back - cfg.main.arrowWingAngleDeg,
        cfg.main.arrowWingLengthM
      );
      const right = destinationPoint(
        placement.lat,
        placement.lng,
        back + cfg.main.arrowWingAngleDeg,
        cfg.main.arrowWingLengthM
      );
      features.main.push(
        lineFeature(
          [
            [left[1], left[0]],
            [placement.lng, placement.lat],
            [right[1], right[0]],
          ],
          color
        )
      );
    }
  } else if (gradientSegments) {
    // One feature per colour window, coloured through a ["get","color"]
    // expression. Deliberately not MapLibre's native line-gradient: that
    // models a single continuous ramp along one line (and needs
    // lineMetrics), which is a different data model from this app's
    // discrete per-window slope colours.
    for (const segment of gradientSegments) {
      features.main.push(
        lineFeature(
          segment.latlngs.map(([lat, lng]) => [lng, lat] as [number, number]),
          segment.color
        )
      );
    }
  } else {
    features.main.push(lineFeature(full, cfg.main.color));
  }

  if (cfg.arrows.enabled) {
    // Point features carrying their own bearing, rather than MapLibre's
    // native symbol-placement:"line" - that spaces symbols in screen pixels,
    // whereas arrows.spacingM is (and should stay) a ground distance.
    for (const placement of computeArrowPlacements(points, cfg.arrows.spacingM)) {
      features.arrows.push({
        type: "Feature",
        properties: { bearing: placement.bearing },
        geometry: { type: "Point", coordinates: [placement.lng, placement.lat] },
      });
    }
  }

  return features;
}

// Leaflet's dashArray is a CSS/SVG dash pattern in pixels; MapLibre's
// line-dasharray is in multiples of the line's own width. Same visual
// pattern, different unit.
const toDashArray = (dashArray: string | null, weight: number): number[] | undefined => {
  if (!dashArray) return undefined;
  const parts = dashArray
    .split(/[\s,]+/)
    .map((part) => parseFloat(part))
    .filter((value) => Number.isFinite(value) && value > 0);
  if (parts.length === 0 || weight <= 0) return undefined;
  return parts.map((value) => value / weight);
};

export interface RouteLinePaint {
  glow: Record<string, unknown>;
  casing: Record<string, unknown>;
  main: Record<string, unknown>;
}

export function buildRouteLinePaint(cfg: RouteStyleConfig): RouteLinePaint {
  const casingDash = toDashArray(cfg.casing.dashArray, cfg.main.weight + cfg.casing.extraWidth);
  const mainDash = toDashArray(cfg.main.dashArray, cfg.main.weight);
  return {
    glow: {
      "line-color": cfg.glow.color,
      "line-width": cfg.main.weight + cfg.glow.blur * 2,
      "line-opacity": cfg.glow.opacity,
      // Leaflet had to fake this with a CSS filter on the SVG element;
      // MapLibre blurs the line itself, so the glow is a closer match here
      // than it was before rather than an approximation.
      "line-blur": cfg.glow.blur,
    },
    casing: {
      "line-color": ["get", "color"],
      "line-width": cfg.main.weight + cfg.casing.extraWidth,
      "line-opacity": cfg.casing.opacity,
      ...(casingDash ? { "line-dasharray": casingDash } : {}),
    },
    main: {
      "line-color": ["get", "color"],
      "line-width": cfg.main.weight,
      "line-opacity": cfg.main.opacity,
      ...(mainDash ? { "line-dasharray": mainDash } : {}),
    },
  };
}

export function buildRouteLineLayout(cfg: RouteStyleConfig): Record<string, unknown> {
  return { "line-cap": cfg.main.lineCap, "line-join": cfg.main.lineJoin };
}

export interface ArrowIconImage {
  width: number;
  height: number;
  data: Uint8Array;
}

/**
 * Rasterises the decorative arrow marker for map.addImage().
 *
 * Drawn straight onto a canvas rather than going through the SVG string
 * routeLineStyle.ts's arrowDivIcon builds: an <img> holding an SVG data URI
 * decodes asynchronously, which would make adding the image a race against
 * the first render. Shape/colour/stroke knobs are the same config either
 * way. Points north at rotation 0, matching icon-rotate's clockwise-from-
 * north bearing.
 */
export function buildArrowIconImage(arrows: RouteStyleArrows, pixelRatio = 2): ArrowIconImage {
  const size = Math.max(1, Math.round(arrows.size * pixelRatio));
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return { width: 1, height: 1, data: new Uint8Array(4) };

  // Same 24x24 viewBox the SVG icon uses, so the coordinates below are the
  // literal path data from arrowDivIcon.
  ctx.scale(size / 24, size / 24);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.globalAlpha = arrows.opacity;
  ctx.strokeStyle = arrows.strokeColor;
  ctx.lineWidth = arrows.strokeWidth;

  ctx.beginPath();
  if (arrows.shape === "chevron") {
    ctx.moveTo(5, 20);
    ctx.lineTo(12, 4);
    ctx.lineTo(19, 20);
    ctx.stroke();
  } else {
    ctx.moveTo(12, 2);
    ctx.lineTo(20, 20);
    ctx.lineTo(12, 15);
    ctx.lineTo(4, 20);
    ctx.closePath();
    ctx.fillStyle = arrows.color;
    ctx.fill();
    ctx.stroke();
  }

  const image = ctx.getImageData(0, 0, size, size);
  return { width: size, height: size, data: new Uint8Array(image.data.buffer) };
}
